import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Header } from "semantic-ui-react";

import Cookies from "js-cookie";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import ModInfo from "react-lib/apps/common/ModInfo";
import SnackMessage from "react-lib/apps/common/SnackMessage";

import ModConfirmOA from "./ModConfirmOA";
import ModInfoOA from "./ModInfoOA";
import UserProfileFormBasic, { UserProfileBasicType } from "./UserProfileFormBasic";
import UserProfileFormDetailed, { UserProfileDetailType } from "./UserProfileFormDetailed";
import UserProfileImage from "./UserProfileImage";

import { formatADtoBEString } from "react-lib/utils/dateUtils";
import { updateEmail, getAuth, signInWithCustomToken } from "firebase/auth";
import { FirebaseError, getApp } from "firebase/app";
import { httpsCallable } from "firebase/functions";

type UserData = UserProfileBasicType & UserProfileDetailType;

type UserProfileProps = {
  controller?: any;
  functionsWithRegion?: any;
  data?: UserData | null;
  emailLogin?: string;
  facebookName?: string;
  isEmailLogin?: boolean;
  isFacebookLogin?: boolean;
  isGoogleLogin?: boolean;
  isLineLogin?: boolean;
  layout?: "basic" | "detailed";
  lineId?: string;
  myDivisions?: Record<string, any>[];
  readOnly?: boolean;
  userId?: number;
  onCreateOA?: () => void;
  onGetDivision?: () => Promise<void>;
  onRefreshUserInfo?: (response: any) => void;
};

const PROFILE_IMAGE_URL = `${process.env.PUBLIC_URL}/static/images/person-placeholder.png`;

const UserProfile = (props: UserProfileProps) => {
  const intl = useIntl();
  const history = useHistory();

  const isMounted = useRef(true);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const [userData, setUserData] = useState<UserData>({});

  const [positionOptions, setPositionOptions] = useState<any[]>([]);
  const [fileImage, setFileImage] = useState<File | null>(null);
  const [openModInfo, setOpenModInfo] = useState(false);
  const [modInfoMsg, setModInfoMsg] = useState("");
  const [openModConfirm, setOpenModConfirm] = useState(false);
  const [isInfoComplete, setIsInfoComplete] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<any>(null);

  const checkInfoComplete = useCallback(
    (data: UserData) =>
      !!data.first_name &&
      !!data.last_name &&
      !!data.first_name_en &&
      !!data.last_name_en &&
      !!data.dob &&
      !!data.email &&
      !!data.phone_no,
    []
  );

  useEffect(() => {
    const getPositionOptions = async ({ apiToken }: any) => {
      const [data] = await props.controller.getUserPosition({
        apiToken,
      });

      if (!data?.items) {
        return;
      }

      const items: Record<string, any>[] = data?.items ?? [];

      const prepare = items
        .filter((item) => item.is_active)
        .filter((item) => item.code)
        .filter((item) => item.name)
        .map((item) => {
          const option: Record<string, any> = {};

          option.key = item.id;
          option.value = item.code;
          option.text = item.name;

          return option;
        });

      setPositionOptions(prepare);
      setUserData((prev) => ({ ...prev, position: prepare[0].value }));
    };

    if (Cookies.get("apiToken") && props.controller?.getUserPosition) {
      void getPositionOptions({ apiToken: Cookies.get("apiToken") });
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const getProfile = async ({ apiToken }: any) => {
      if (props.data) {
        setUserData({ ...props.data, birthdate: formatADtoBEString(props.data.dob) });

        return;
      }

      const promiseArr = [
        props.controller.getUserProfile({
          apiToken,
        }),
        props.controller.getOAUserProfile({
          apiToken,
        }),
      ];

      if (props.controller.getUserAccount && props.userId) {
        promiseArr.push(props.controller.getUserEmployee({ id: props.userId }));
      }

      const [[profile], [profileOA], employee] = await Promise.all(promiseArr);

      if (profile) {
        props.controller.getUserEmployee({
          id: profile.user,
          apiToken,
        });

        setUserData((prev) => ({
          ...prev,
          cid: profile.cid,
          email: profile.email,
          first_name: profile.first_name,
          first_name_en: profile.first_name_en,
          last_name: profile.last_name,
          last_name_en: profile.last_name_en,
          phone_no: profile.phone_no,
          user: profile.user,
        }));
      }

      if (profileOA) {
        setUserData((prev) => ({
          ...prev,
          licence: profileOA.licence,
          position: profileOA.position,
        }));
      }

      if (employee?.[0]) {
        setUserData((prev) => ({
          ...prev,
          cid: employee[0].citizen_no,
          code: employee[0].code,
          status: employee[0].status,
        }));
      }

      const image = profileOA?.image || profile?.image_url;

      setUserData((prev) => ({ ...prev, image }));
      setIsInfoComplete(checkInfoComplete({ ...profile, ...profileOA }));

      if (profile?.dob) {
        setUserData((prev) => ({ ...prev, birthdate: formatADtoBEString(profile.dob) }));
      }
    };

    if (Cookies.get("apiToken") && props.controller?.getUserProfile) {
      void getProfile({ apiToken: Cookies.get("apiToken") });
    }
  }, []);

  useEffect(() => {
    void props.onGetDivision?.();
  }, []);

  // Login info as useMemo
  const loginInfo = useMemo(() => {
    const isJustLogin =
      !!props.isLineLogin ||
      !!props.isFacebookLogin ||
      !!props.isEmailLogin ||
      !!props.isGoogleLogin;

    let loginDisplay = {
      label: "",
      method: "",
      value: "",
    };

    if (props.isLineLogin) {
      loginDisplay = {
        label: "Line ID",
        method: "LINE",
        value: props.lineId ?? "",
      };
    } else if (props.isFacebookLogin) {
      loginDisplay = {
        label: "Facebook Name",
        method: "Facebook",
        value: props.facebookName ?? "",
      };
    } else if (props.isEmailLogin) {
      loginDisplay = {
        label: "Email:",
        method: "Email",
        value: props.emailLogin ?? "",
      };
    } else if (props.isGoogleLogin) {
      loginDisplay = {
        label: "Google:",
        method: "Google",
        value: "",
      };
    }

    return {
      emailLogin: props.emailLogin,
      facebookName: props.facebookName,
      isEmailLogin: props.isEmailLogin,
      isFacebookLogin: props.isFacebookLogin,
      isJustLogin,
      isLineLogin: props.isLineLogin,
      lineId: props.lineId,
      loginDisplay,
    };
  }, [
    props.emailLogin,
    props.facebookName,
    props.isEmailLogin,
    props.isFacebookLogin,
    props.isLineLogin,
    props.lineId,
  ]);

  async function reauthWithCustomToken(functions) {
    const app = getApp()
    const auth = getAuth();

    const uid = auth.currentUser!.uid;
    const createToken = httpsCallable(functions, "createTokenFromUser"); // your callable that returns { token }
    const { data } = await createToken( uid );
    await signInWithCustomToken(auth, data as string); // now “recent login”
  }


  const handleSave = async () => {
    const {
      birthdate,
      cid,
      code,
      email,
      first_name: firstName,
      first_name_en: firstNameEn,
      last_name: lastName,
      last_name_en: lastNameEn,
      licence,
      phone_no: phone,
      position,
      status,
      user,
    } = userData;

    const fieldChecks = [
      [firstName, "ชื่อ"],
      [lastName, "นามสกุล"],
      [birthdate, "วันเกิด"],
    ];

    if (props.layout === "detailed") {
      fieldChecks.push(
        [email, "Email"],
        [phone, "เบอร์โทร"],
        [firstNameEn, "Firstname"],
        [lastNameEn, "Lastname"]
      );
    } else {
      fieldChecks.push([position, "ตำแหน่ง"], [licence, "เลขที่ใบประกอบ"]);
    }

    const missingFields = fieldChecks.filter(([field]) => !field).map(([, name]) => name);

    if (missingFields.length > 0) {
      alert(`กรุณาระบุฟิลด์จำเป็น ${missingFields.join(" ")}`);

      return;
    }

    setIsSaveLoading(true);

    const positionCode = position || "TEMP";
    const positionId = positionOptions.find((option) => option.value === positionCode)?.key;

    const data = {
      apiToken: Cookies.get("apiToken"),
      cid,
      code: code || `ISH-${user}`,
      dob: birthdate,
      email,
      firstName,
      firstNameEn,
      image: fileImage,
      lastName,
      lastNameEn,
      licence,
      phone,
      position: positionCode,
      positionId,
      status: status || "ACTIVE",
      user,
    };

    if (props.controller.updateUserEmployee) {
      const [, employeeError] = await props.controller.updateUserEmployee(data);

      if (employeeError) {
        setErrorMessage(employeeError);
        setIsSaveLoading(false);

        return;
      }

      let auth = getAuth()
      // Update Email to firebase  
      console.log('auth: ', auth);
      if (auth?.currentUser && email) {
        updateEmail(auth?.currentUser, email).then(() => {
          console.log("Email updated successfully");
        }).catch(async (error) => {
          if (error instanceof FirebaseError) {
            switch (error.code) {
              case "auth/requires-recent-login":
                await reauthWithCustomToken(props.functionsWithRegion)
                auth = getAuth()
                if (auth.currentUser !== null) {
                  updateEmail(auth.currentUser, email).then(( ) => { 
                    console.log("Email updated successfully")
                  }).catch((error) => {
                    console.log("error: ", error )
                  })
                }
                break;
              case "auth/operation-not-allowed":
              default:
                console.log('error: ', error);
            }
          }
        }
        );
      }
    }

    const [profileRes, profileError] = await props.controller.updateUserProfile(data);

    const [profileOARes, profileOAError] = await props.controller.patchOAUserProfile(data);

    setIsSaveLoading(false);

    if (profileError || profileOAError) {
      setModInfoMsg("เกิดข้อผิดพลาด");
    } else {
      setModInfoMsg("บันทึกสำเร็จ");
      props.onRefreshUserInfo?.(profileRes);
    }

    setUserData((prev) => ({ ...prev, image: profileOARes?.image || "" }));

    if (props.layout === "detailed" && !isInfoComplete) {
      setOpenModConfirm(true);
      setIsInfoComplete(false);

      return;
    }

    setOpenModInfo(true);

    console.log(" isLineLogin", props.isLineLogin);
    console.log(" isFacebookLogin", props.isFacebookLogin);
    console.log(" isEmailLogin", props.isEmailLogin);

    const isNotLoggedIn = !props.isLineLogin && !props.isFacebookLogin && !props.isEmailLogin;

    // If came from Login Page goto ShowDivision
    if (isNotLoggedIn || props.layout === "detailed") {
      return;
    }

    // push to next page

    // Logic Same Condition##Goto #2
    // API that check request
    const [listDivision] = await props.controller.getListOARequestCheck({
      apiToken: Cookies.get("apiToken"),
    });

    if (listDivision?.items?.length > 0) {
      // props.controller.setState({ messageShowPending: "listDivision" });
      // Goto Waiting if have pending...
      console.log("handleLoginSuccess Goto /ShowDivision  from Login success");
      history.push({ pathname: "/ShowDivision" });

      return;
    }

    console.log("handleLoginSuccess props.myDivisions", props.myDivisions);

    // No Division
    if (
      (props.myDivisions?.length === 1 && props.myDivisions[0].code === "create_oa") ||
      (props.myDivisions ?? []).length === 0
    ) {
      console.log("handleLoginSuccess Goto /CreateOA  from Login success");
      history.push({ pathname: "/CreateOA" });

      return;
    }

    if ((props.myDivisions ?? []).length > 1) {
      console.log("handleLoginSuccess Goto /Chat  from Login success");
      history.push({ pathname: "/Chat" });
    }
  };

  // Handle user data changes
  const handleChangeValue = (e: any, data: any) => {
    setUserData((prev) => ({ ...prev, [data.name]: data.value }));
  };

  const handleImageChange = (file: File, fileUrl: string) => {
    setFileImage(file);

    setUserData((prev) => ({ ...prev, image: fileUrl }));
  };

  const handleClearImage = () => {
    setUserData((prev) => ({ ...prev, image: "" }));
  };

  const handleCloseModConfirm = () => {
    setOpenModConfirm(false);
  };

  return (
    <>
      <SnackMessage
        error={errorMessage}
        success={null}
        onClose={() => {
          setErrorMessage(null);
        }}
      />

      {openModInfo &&
        (props.layout === "detailed" ? (
          <ModInfoOA
            open={!openModConfirm}
            type={modInfoMsg.includes("บันทึกสำเร็จ") ? "success" : "error"}
            onApprove={() => {
              setOpenModInfo(false);
            }}
            onClose={handleCloseModConfirm}
          />
        ) : (
          <ModInfo
            alertText={modInfoMsg}
            btnText={intl.formatMessage({ id: "ตกลง" })}
            open={openModInfo}
            titleColor={modInfoMsg.includes("บันทึกสำเร็จ") ? "green" : "red"}
            onApprove={() => {
              setOpenModInfo(false);
            }}
            titleName={
              modInfoMsg.includes("บันทึกสำเร็จ") ? "บันทึกเรียบร้อย" : "เกิดข้อผิดพลาด !!"
            }
          />
        ))}

      <ModConfirmOA
        approveButtonText="ต้องการสร้าง"
        denyButtonText="ไว้ทีหลัง"
        open={openModConfirm}
        subtitle="ต้องการสร้าง Official Account เลยหรือไม่"
        title="สร้างข้อมูลส่วนตัวสำเร็จ"
        onApprove={props.onCreateOA}
        onClose={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
      />

      <div className="register-new-user">
        <UserProfileImage
          readOnly={props.readOnly}
          src={userData.image || PROFILE_IMAGE_URL}
          onChange={handleImageChange}
          onClear={handleClearImage}
        />
        <div className="vl" />
        <div className="profile-info">
          <Header style={{ color: "var(--primary-theme-color)", margin: "1.25rem 0 2rem" }} as="h1">
            ข้อมูลส่วนตัว
          </Header>

          {props.layout === "detailed" ? (
            <UserProfileFormDetailed
              isSaveLoading={isSaveLoading}
              loginInfo={loginInfo}
              readOnly={props.readOnly}
              userData={userData}
              onChangeValue={handleChangeValue}
              onSave={handleSave}
            />
          ) : (
            <UserProfileFormBasic
              isSaveLoading={isSaveLoading}
              loginInfo={loginInfo}
              userData={userData}
              positionOptions={positionOptions}
              onChangeValue={handleChangeValue}
              onSave={handleSave}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default UserProfile;
